import React, { useEffect, useMemo, useState } from 'react';
import { Wallet, MapPin, Flag, Article, BookmarkSimple, SealCheck, Star, Users, MagnifyingGlass as SearchIcon, CheckCircle, Circle, Sparkle, X } from '@phosphor-icons/react';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { MarketplaceFilters } from '../components/marketplace/MarketplaceFilters';
import { AuthGateModal, AuthGateIntent } from '../components/marketplace/AuthGateModal';
import { PAGE_ROUTE } from '../lib/routes';
import { HOUSING_TYPES } from '../lib/constants';
import { cn, getSession, isBookmarked, toggleBookmark, formatBudget, isFullyVerified, comboGroupLabel, hasSuccesOfferTo, getActiveProperties } from '../lib/utils';
import { AvatarStack } from '../components/ui/AvatarStack';
import { getBookmarks } from '../lib/utils';
import { MarketplaceFilters as FiltersType, DEFAULT_FILTERS, filterProfiles, sortProfiles, filtersFromProperty } from '../lib/marketplaceFilters';
interface MarketplaceProps {
  navigate: (page: string, context?: any) => void;
}
type ComboMember = {
  name: string;
  avatar: string;
  slug?: string;
};
type Profile = {
  id: string;
  slug: string;
  firstName: string;
  lastName: string;
  birthYear: number;
  occupation: string;
  city: string;
  budgetMin: number;
  budgetMax: number;
  budgetCurrency: 'EUR' | 'RON';
  housingType: string;
  coupleStatus: 'singur' | 'cuplu';
  isSmoker: boolean;
  hasPets: boolean;
  petDetails: string;
  profilePhoto: string;
  profileViews: number;
  verifications: {
    email: boolean;
    identity: boolean;
    income: boolean;
  };
  rating: number;
  neighborhoods: string;
  moveInDate: string;
  contractDuration: string;
  rooms: string;
  floor: string;
  yearBuiltLabel: string;
  heating: string;
  amenities: string[];
  profileStatus?: 'active' | 'inactive';
  // Combo-rent (MVP, invite-only finalized groups)
  // comboMembers includes the creator as the first member. When members.length
  // >= 2, the card is rendered as a group. When < 2, the listing falls back to
  // the regular solo presentation automatically.
  comboMembers?: ComboMember[];
  // True only when every member of the group is individually verified. We
  // never infer this from the creator's own verifications.
  comboAllVerified?: boolean;
};
const MOCK_PROFILES: Profile[] = [{
  id: '1',
  slug: 'olivia-p',
  firstName: 'Olivia',
  lastName: 'P.',
  birthYear: 1995,
  occupation: 'Freelancer',
  city: 'București',
  budgetMin: 500,
  budgetMax: 650,
  budgetCurrency: 'EUR',
  housingType: 'apartament_bloc',
  coupleStatus: 'cuplu',
  isSmoker: false,
  hasPets: true,
  petDetails: '1 pisică',
  profilePhoto: 'https://i.pravatar.cc/150?img=5',
  profileViews: 142,
  verifications: {
    email: true,
    identity: true,
    income: true
  },
  rating: 4.8,
  neighborhoods: 'Floreasca, Aviatorilor',
  moveInDate: 'Octombrie, 2026',
  contractDuration: '1 an',
  rooms: '3 cam',
  floor: 'Etaj 1-3',
  yearBuiltLabel: '> 1990',
  heating: 'Centrală',
  amenities: ['Parcare', 'Mobilat', 'Balcon', 'Lift', 'AC', 'Boxă'],
  profileStatus: 'active'
}, {
  id: '2',
  slug: 'andrei-popescu',
  firstName: 'Andrei',
  lastName: 'P.',
  birthYear: 1996,
  occupation: 'Product Designer',
  city: 'București',
  budgetMin: 400,
  budgetMax: 550,
  budgetCurrency: 'EUR',
  housingType: 'apartament_bloc',
  coupleStatus: 'singur',
  isSmoker: false,
  hasPets: false,
  petDetails: '',
  profilePhoto: 'https://i.pravatar.cc/150?img=11',
  profileViews: 142,
  verifications: {
    email: true,
    identity: true,
    income: false
  },
  rating: 4.6,
  neighborhoods: 'Cotroceni, Militari',
  moveInDate: 'Iunie, 2026',
  contractDuration: '2 ani',
  rooms: '2 cam',
  floor: 'Etaj 2-5',
  yearBuiltLabel: '> 2000',
  heating: 'Centrală',
  amenities: ['Mobilat', 'AC', 'Balcon'],
  profileStatus: 'active'
}, {
  id: '3',
  slug: 'maria-ionescu',
  firstName: 'Maria',
  lastName: 'I.',
  birthYear: 1992,
  occupation: 'Marketing Manager',
  city: 'Cluj-Napoca',
  budgetMin: 600,
  budgetMax: 800,
  budgetCurrency: 'EUR',
  housingType: 'apartament_bloc',
  coupleStatus: 'cuplu',
  isSmoker: false,
  hasPets: true,
  petDetails: '1 pisică',
  profilePhoto: 'https://i.pravatar.cc/150?img=9',
  profileViews: 89,
  verifications: {
    email: true,
    identity: false,
    income: false
  },
  rating: 4.9,
  neighborhoods: 'Centru, Andrei Mureșanu',
  moveInDate: 'August, 2026',
  contractDuration: '1 an',
  rooms: '3 cam',
  floor: 'Etaj 1-4',
  yearBuiltLabel: '> 1995',
  heating: 'Centrală',
  amenities: ['Parcare', 'Mobilat', 'Lift', 'Balcon'],
  profileStatus: 'inactive'
}, {
  id: '4',
  slug: 'alexandru-radu',
  firstName: 'Alexandru',
  lastName: 'R.',
  birthYear: 1999,
  occupation: 'Student',
  city: 'Timișoara',
  budgetMin: 250,
  budgetMax: 350,
  budgetCurrency: 'EUR',
  housingType: 'garsoniera',
  coupleStatus: 'singur',
  isSmoker: false,
  hasPets: false,
  petDetails: '',
  profilePhoto: 'https://i.pravatar.cc/150?img=12',
  profileViews: 56,
  verifications: {
    email: false,
    identity: false,
    income: false
  },
  rating: 4.2,
  neighborhoods: 'Iosefin, Centru',
  moveInDate: 'Septembrie, 2026',
  contractDuration: '10 luni',
  rooms: '1 cam',
  floor: 'Orice etaj',
  yearBuiltLabel: '> 1980',
  heating: 'Centrală',
  amenities: ['Mobilat', 'Internet'],
  profileStatus: 'active'
}, {
  id: '5',
  slug: 'elena-dumitrescu',
  firstName: 'Elena',
  lastName: 'D.',
  birthYear: 1994,
  occupation: 'Medic',
  city: 'Brașov',
  budgetMin: 550,
  budgetMax: 750,
  budgetCurrency: 'EUR',
  housingType: 'casa_vila',
  coupleStatus: 'cuplu',
  isSmoker: false,
  hasPets: true,
  petDetails: '1 câine',
  profilePhoto: 'https://i.pravatar.cc/150?img=8',
  profileViews: 203,
  verifications: {
    email: true,
    identity: true,
    income: true
  },
  rating: 5.0,
  neighborhoods: 'Răcădău, Schei',
  moveInDate: 'Noiembrie, 2026',
  contractDuration: '2 ani',
  rooms: '4 cam',
  floor: 'Casă',
  yearBuiltLabel: '> 2010',
  heating: 'Centrală',
  amenities: ['Parcare', 'Curte', 'Garaj', 'Mobilat'],
  profileStatus: 'active'
}, {
  id: '6',
  slug: 'mihai-constantinescu',
  firstName: 'Mihai',
  lastName: 'C.',
  birthYear: 1989,
  occupation: 'Inginer Software',
  city: 'Iași',
  budgetMin: 500,
  budgetMax: 700,
  budgetCurrency: 'EUR',
  housingType: 'apartament_bloc',
  coupleStatus: 'singur',
  isSmoker: false,
  hasPets: false,
  petDetails: '',
  profilePhoto: 'https://i.pravatar.cc/150?img=13',
  profileViews: 178,
  verifications: {
    email: true,
    identity: true,
    income: true
  },
  rating: 4.7,
  neighborhoods: 'Copou, Centru',
  moveInDate: 'Iulie, 2026',
  contractDuration: '1 an',
  rooms: '2 cam',
  floor: 'Etaj 3+',
  yearBuiltLabel: '> 2005',
  heating: 'Centrală',
  amenities: ['Parcare', 'Mobilat', 'AC', 'Lift'],
  profileStatus: 'active'
}, {
  id: '7',
  slug: 'ana-munteanu',
  firstName: 'Ana',
  lastName: 'M.',
  birthYear: 1997,
  occupation: 'Freelancer',
  city: 'București',
  budgetMin: 450,
  budgetMax: 600,
  budgetCurrency: 'EUR',
  housingType: 'garsoniera',
  coupleStatus: 'singur',
  isSmoker: false,
  hasPets: true,
  petDetails: '2 pisici',
  profilePhoto: 'https://i.pravatar.cc/150?img=16',
  profileViews: 95,
  verifications: {
    email: true,
    identity: false,
    income: false
  },
  rating: 4.4,
  neighborhoods: 'Tineretului, Unirii',
  moveInDate: 'Mai, 2026',
  contractDuration: '1 an',
  rooms: '1 cam',
  floor: 'Etaj 2+',
  yearBuiltLabel: '> 2000',
  heating: 'Centrală',
  amenities: ['Mobilat', 'Lift'],
  profileStatus: 'active'
}, {
  id: '8',
  slug: 'ioana-stan',
  firstName: 'Ioana',
  lastName: 'S.',
  birthYear: 1995,
  occupation: 'Arhitect',
  city: 'Timișoara',
  budgetMin: 500,
  budgetMax: 700,
  budgetCurrency: 'EUR',
  housingType: 'apartament_bloc',
  coupleStatus: 'cuplu',
  isSmoker: false,
  hasPets: false,
  petDetails: '',
  profilePhoto: 'https://i.pravatar.cc/150?img=20',
  profileViews: 134,
  verifications: {
    email: true,
    identity: true,
    income: true
  },
  rating: 4.8,
  neighborhoods: 'Iosefin, Fabric',
  moveInDate: 'Septembrie, 2026',
  contractDuration: '2 ani',
  rooms: '3 cam',
  floor: 'Etaj 1-5',
  yearBuiltLabel: '> 1995',
  heating: 'Centrală',
  amenities: ['Parcare', 'Mobilat', 'Balcon', 'Lift'],
  profileStatus: 'active'
},
// ── Combo / Grup mocks (MVP: invite-only finalized groups) ────────────
// 9 — Combo (2 friends sharing). Not fully verified as a group.
{
  id: '9',
  slug: 'andrei-combo',
  firstName: 'Andrei',
  lastName: 'M.',
  birthYear: 1998,
  occupation: 'Software Engineer',
  city: 'București',
  budgetMin: 400,
  budgetMax: 550,
  budgetCurrency: 'EUR',
  housingType: 'apartament_bloc',
  coupleStatus: 'singur',
  isSmoker: false,
  hasPets: false,
  petDetails: '',
  profilePhoto: 'https://i.pravatar.cc/150?img=14',
  profileViews: 64,
  verifications: {
    email: true,
    identity: true,
    income: false
  },
  rating: 4.5,
  neighborhoods: 'Tineretului, Unirii',
  moveInDate: 'August, 2026',
  contractDuration: '1 an',
  rooms: '2 cam',
  floor: 'Etaj 1-5',
  yearBuiltLabel: '> 2000',
  heating: 'Centrală',
  amenities: ['Mobilat', 'Lift', 'AC'],
  profileStatus: 'active',
  comboMembers: [{
    name: 'Andrei M.',
    avatar: 'https://i.pravatar.cc/150?img=14',
    slug: 'andrei-combo'
  }, {
    name: 'Tudor L.',
    avatar: 'https://i.pravatar.cc/150?img=15',
    slug: 'andrei-popescu'
  }],
  comboAllVerified: false
},
// 10 — Grup de 3, fully verified
{
  id: '10',
  slug: 'combo-pair',
  firstName: 'Sara',
  lastName: 'V.',
  birthYear: 1996,
  occupation: 'Designer',
  city: 'București',
  budgetMin: 600,
  budgetMax: 900,
  budgetCurrency: 'EUR',
  housingType: 'apartament_bloc',
  coupleStatus: 'singur',
  isSmoker: false,
  hasPets: true,
  petDetails: '1 pisică',
  profilePhoto: 'https://i.pravatar.cc/150?img=47',
  profileViews: 88,
  verifications: {
    email: true,
    identity: true,
    income: true
  },
  rating: 4.7,
  neighborhoods: 'Cotroceni, Centru',
  moveInDate: 'Iulie, 2026',
  contractDuration: '2 ani',
  rooms: '3 cam',
  floor: 'Etaj 2-5',
  yearBuiltLabel: '> 2000',
  heating: 'Centrală',
  amenities: ['Parcare', 'Mobilat', 'Balcon', 'Lift'],
  profileStatus: 'active',
  comboMembers: [{
    name: 'Sara V.',
    avatar: 'https://i.pravatar.cc/150?img=47',
    slug: 'combo-pair'
  }, {
    name: 'Tudor L.',
    avatar: 'https://i.pravatar.cc/150?img=15',
    slug: 'andrei-popescu'
  }, {
    name: 'Bianca R.',
    avatar: 'https://i.pravatar.cc/150?img=44',
    slug: 'maria-ionescu'
  }],
  comboAllVerified: true
},
// 11 — Grup de 4, partially verified
{
  id: '11',
  slug: 'combo-grup',
  firstName: 'Cristian',
  lastName: 'D.',
  birthYear: 1994,
  occupation: 'Marketing',
  city: 'București',
  budgetMin: 1000,
  budgetMax: 1400,
  budgetCurrency: 'EUR',
  housingType: 'apartament_bloc',
  coupleStatus: 'singur',
  isSmoker: false,
  hasPets: false,
  petDetails: '',
  profilePhoto: 'https://i.pravatar.cc/150?img=33',
  profileViews: 120,
  verifications: {
    email: true,
    identity: true,
    income: true
  },
  rating: 4.6,
  neighborhoods: 'Floreasca, Aviației',
  moveInDate: 'Septembrie, 2026',
  contractDuration: '1 an',
  rooms: '4 cam',
  floor: 'Etaj 1-5',
  yearBuiltLabel: '> 2010',
  heating: 'Centrală',
  amenities: ['Parcare', 'Mobilat', 'Balcon', 'Lift', 'AC'],
  profileStatus: 'active',
  comboMembers: [{
    name: 'Cristian D.',
    avatar: 'https://i.pravatar.cc/150?img=33',
    slug: 'combo-grup'
  }, {
    name: 'Maria P.',
    avatar: 'https://i.pravatar.cc/150?img=48',
    slug: 'maria-ionescu'
  }, {
    name: 'Vlad T.',
    avatar: 'https://i.pravatar.cc/150?img=51',
    slug: 'mihai-constantinescu'
  }, {
    name: 'Andrei B.',
    avatar: 'https://i.pravatar.cc/150?img=53',
    slug: 'andrei-popescu'
  }],
  comboAllVerified: false
}];
function ProfileCard({
  profile,
  onClick,
  onBookmarkBlocked
}: {
  profile: Profile;
  onClick: () => void;
  onBookmarkBlocked: () => void;
}) {
  const [bookmarked, setBookmarked] = useState(() => isBookmarked(profile.slug));
  const fullyVerified = isFullyVerified(profile.verifications);
  const handleBookmarkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const session = getSession();
    if (!session) {
      onBookmarkBlocked();
      return;
    }
    const nowSaved = toggleBookmark({
      slug: profile.slug,
      firstName: profile.firstName,
      lastName: profile.lastName,
      occupation: profile.occupation,
      city: profile.city,
      neighborhoods: profile.neighborhoods,
      profilePhoto: profile.profilePhoto,
      isVerified: fullyVerified,
      budgetMin: profile.budgetMin,
      budgetMax: profile.budgetMax,
      budgetCurrency: profile.budgetCurrency,
      status: profile.profileStatus
    });
    setBookmarked(nowSaved);
  };
  const housingLabel = HOUSING_TYPES.find(t => t.value === profile.housingType)?.label || 'Apartament';
  // Build the secondary pill list
  const secondaryPills: string[] = [profile.rooms, profile.floor, profile.yearBuiltLabel, profile.heating, ...profile.amenities];
  const VISIBLE_PILLS = 6;
  const visible = secondaryPills.slice(0, VISIBLE_PILLS);
  const overflowCount = secondaryPills.length - visible.length;
  // ── Group state ───────────────────────────────────────────────────────
  // A listing is "a group" only when ≥2 members are confirmed. A 1-member
  // combo entry is just a solo listing and falls back to the regular UI.
  const memberCount = profile.comboMembers?.length || 0;
  const isGroup = memberCount >= 2;
  const groupLabel = isGroup ? comboGroupLabel(memberCount) : null;
  // Verified seal: for groups, only show when the whole group is verified.
  const showVerified = isGroup ? !!profile.comboAllVerified : fullyVerified;
  // Composition row: hide for groups unless there's something group-wide
  // worth showing (currently: pets). For solo listings, always show.
  const composition = [profile.coupleStatus === 'cuplu' ? 'Cuplu' : 'Singur', profile.isSmoker ? 'Fumător' : 'Nefumători', profile.hasPets ? profile.petDetails || 'Animale' : null].filter(Boolean).join(' · ');
  const showComposition = !isGroup || profile.hasPets;
  return <button onClick={onClick} className="group block text-left outline-none focus-visible:ring-2 focus-visible:ring-lime-1 rounded-2xl w-full h-full" data-id="element-3654">
      <article className="rounded-2xl border border-dark-4 overflow-hidden hover:border-dark-4/80 transition-colors duration-200 h-full flex flex-col" data-id="element-3655">
        {/* Header — dark-1 bg, 24px padding. items-center keeps the
            avatar (or stack) balanced with the name + sub-line; bookmark
            uses self-start to stay top-pinned. */}
        <header className="bg-dark-1 px-6 py-6 flex items-center gap-4" data-id="element-3656">
          {isGroup ? <AvatarStack members={profile.comboMembers!} size="md" className="shrink-0" data-id="element-3657" /> : profile.profilePhoto ? <img src={profile.profilePhoto} alt={`${profile.firstName} ${profile.lastName}`} className="w-12 h-12 rounded-full object-cover shrink-0" data-id="element-3658" /> : <div className="w-12 h-12 rounded-full bg-dark-3 flex items-center justify-center text-base font-medium text-light-2 shrink-0" data-id="element-3659">
              {profile.firstName[0]}
            </div>}

          <div className="min-w-0 flex-1" data-id="element-3660">
            <div className="flex items-center gap-1.5" data-id="element-3661">
              <h3 className="text-xl font-normal text-light-1 truncate leading-tight" data-id="element-3662">
                {profile.firstName} {profile.lastName}
              </h3>
              {showVerified && <SealCheck size={18} weight="fill" className="text-lime-1 shrink-0" aria-label={isGroup ? 'Toți membrii grupului sunt verificați' : 'Profil verificat complet'} data-id="element-3663" />}
            </div>
            <div className="flex items-center gap-1.5 text-sm font-normal text-light-3 mt-1" data-id="element-3664">
              {isGroup && <Users size={14} weight="fill" className="text-light-3 shrink-0" aria-hidden="true" data-id="element-3665" />}
              <span className="truncate" data-id="element-3666">
                {groupLabel || profile.occupation}
              </span>
              {!isGroup && <>
                  <span className="text-light-4" data-id="element-3667">·</span>
                  <Star size={12} weight="fill" className="text-light-3 shrink-0" data-id="element-3668" />
                  <span data-id="element-3669">{profile.rating.toFixed(1)}</span>
                </>}
            </div>
          </div>

          <button type="button" onClick={handleBookmarkClick} className="text-light-3 hover:text-light-1 transition-colors p-1 -m-1 cursor-pointer shrink-0 self-start" aria-label={bookmarked ? 'Elimină din salvate' : 'Salvează'} data-id="element-3670">
            <BookmarkSimple size={22} weight={bookmarked ? 'fill' : 'regular'} className={bookmarked ? 'text-lime-1' : ''} data-id="element-3671" />
          </button>
        </header>

        {/* Content — dark-2 bg, 24px L/R padding, 32px T/B */}
        <div className="bg-dark-2 px-6 py-8 space-y-8 flex-1" data-id="element-3672">
          {/* Info list */}
          <ul className="space-y-3 text-sm font-normal text-light-2" data-id="element-3673">
            <li className="flex items-center gap-3" data-id="element-3674">
              <Wallet size={18} weight="regular" className="text-light-2 shrink-0" data-id="element-3675" />
              <span data-id="element-3676">{formatBudget(profile.budgetMax)}</span>
            </li>
            <li className="flex items-center gap-3" data-id="element-3677">
              <MapPin size={18} weight="regular" className="text-light-2 shrink-0" data-id="element-3678" />
              <span className="truncate" data-id="element-3679">{profile.neighborhoods}</span>
            </li>
            <li className="flex items-center gap-3" data-id="element-3680">
              <Flag size={18} weight="regular" className="text-light-2 shrink-0" data-id="element-3681" />
              <span data-id="element-3682">
                {profile.moveInDate} · {profile.contractDuration}
              </span>
            </li>
            {showComposition && <li className="flex items-center gap-3" data-id="element-3683">
                <Article size={18} weight="regular" className="text-light-2 shrink-0" data-id="element-3684" />
                <span className="truncate" data-id="element-3685">{composition}</span>
              </li>}
          </ul>

          {/* Pills */}
          <div className="flex flex-wrap gap-2" data-id="element-3686">
            {/* Featured pill — housing type (only filled one) */}
            <span className={cn('inline-flex items-center h-10 px-4 rounded-full text-sm font-normal', 'bg-dark-4 text-light-1 border border-dark-4')} data-id="element-3687">
              {housingLabel}
            </span>

            {visible.map(pill => <span key={pill} className="inline-flex items-center h-10 px-4 rounded-full text-sm font-normal border border-dark-4 text-light-2" data-id="element-3688">
                {pill}
              </span>)}

            {overflowCount > 0 && <span className="inline-flex items-center h-10 px-2 text-sm font-normal text-light-3" data-id="element-3689">
                + {overflowCount} altele
              </span>}
          </div>
        </div>
      </article>
    </button>;
}
function OnboardingCard({
  onDismiss
}: {
  onDismiss: () => void;
}) {
  const session = getSession();
  const firstName = session?.name?.split(' ')[0] || 'Proprietar';
  // Check completion states
  const hasExplored = true; // Always true since they're on Marketplace
  const hasSavedProfiles = (() => {
    try {
      const bookmarks = localStorage.getItem('izio_bookmarks');
      return bookmarks ? JSON.parse(bookmarks).length > 0 : false;
    } catch {
      return false;
    }
  })();
  const hasCompletedProfile = false;
  const steps = [{
    id: 'explore',
    title: 'Explorează chiriașii activi',
    description: 'Descoperă profilurile disponibile pe platformă',
    completed: hasExplored
  }, {
    id: 'save',
    title: 'Salvează profile pentru mai târziu',
    description: 'Creează o listă cu chiriașii care te interesează',
    completed: hasSavedProfiles
  }, {
    id: 'profile',
    title: 'Completează profilul',
    description: 'Crește încrederea chiriașilor — profilurile complete au șanse mai mari ca ofertele să fie acceptate.',
    completed: hasCompletedProfile
  }];
  return <div className="bg-dark-2 border border-lime-4 rounded-2xl p-6 mb-8" data-id="element-3690">
      <div className="flex items-start justify-between gap-4 mb-6" data-id="element-3691">
        <div data-id="element-3692">
          <div className="flex items-center gap-2 mb-1" data-id="element-3693">
            <Sparkle size={18} weight="fill" className="text-lime-1 shrink-0" data-id="element-3694" />
            <h2 className="text-xl font-semibold text-light-1" data-id="element-3695">
              Bine ai venit pe Izio, {firstName}!
            </h2>
          </div>
          <p className="text-sm text-light-3" data-id="element-3696">Iată cum poți începe:</p>
        </div>
        <button type="button" onClick={onDismiss} aria-label="Închide" className="shrink-0 p-1 rounded-md text-light-3 hover:text-light-1 hover:bg-dark-3 transition-colors cursor-pointer" data-id="element-3697">
          <X size={18} data-id="element-3698" />
        </button>
      </div>
      <div className="space-y-4" data-id="element-3699">
        {steps.map(step => <div key={step.id} className="flex items-start gap-3" data-id="element-3700">
            {step.completed ? <CheckCircle size={20} weight="fill" className="text-lime-1 shrink-0 mt-0.5" data-id="element-3701" /> : <Circle size={20} weight="regular" className="text-light-4 shrink-0 mt-0.5" data-id="element-3702" />}
            <div className="flex-1 min-w-0" data-id="element-3703">
              <h3 className="text-sm font-medium text-light-1" data-id="element-3704">{step.title}</h3>
              <p className="text-xs text-light-3 mt-1 leading-relaxed" data-id="element-3705">
                {step.description}
              </p>
            </div>
          </div>)}
      </div>
    </div>;
}
export function Marketplace({
  navigate
}: MarketplaceProps) {
  const session = getSession();
  const isSupply = session?.role === 'supply';
  // Load initial state from localStorage
  const [filters, setFilters] = useState<FiltersType>(() => {
    try {
      const saved = localStorage.getItem('izio_marketplace_filters_v2');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...DEFAULT_FILTERS,
          ...parsed.filters
        };
      }
    } catch {}
    return DEFAULT_FILTERS;
  });
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(() => {
    try {
      const saved = localStorage.getItem('izio_marketplace_filters_v2');
      if (saved) return JSON.parse(saved).selectedPropertyId;
    } catch {}
    return null;
  });
  const [authGate, setAuthGate] = useState<{
    open: boolean;
    intent: AuthGateIntent;
  }>({
    open: false,
    intent: 'bookmark'
  });
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => {
    try {
      return localStorage.getItem('marketplace_onboarding_dismissed_supply') === '1';
    } catch {
      return false;
    }
  });
  const handleDismissOnboarding = () => {
    setOnboardingDismissed(true);
    try {
      localStorage.setItem('marketplace_onboarding_dismissed_supply', '1');
    } catch {
      // noop
    }
  };
  const showOnboarding = session?.role === 'supply' && !onboardingDismissed;
  // Save to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem('izio_marketplace_filters_v2', JSON.stringify({
        filters,
        selectedPropertyId
      }));
    } catch {}
  }, [filters, selectedPropertyId]);
  const properties = isSupply ? getActiveProperties(session?.email) : [];
  const handleSelectProperty = (id: string | null) => {
    setSelectedPropertyId(id);
    if (id) {
      const prop = properties.find(p => p.id === id);
      if (prop) {
        const newFilters = {
          ...filters,
          ...filtersFromProperty(prop),
          sort: 'match'
        };
        setFilters(newFilters);
      }
    } else {
      setFilters({
        ...filters,
        sort: 'newest'
      });
    }
  };
  const handleClearAll = () => {
    setFilters(DEFAULT_FILTERS);
    setSelectedPropertyId(null);
  };
  const filteredProfiles = useMemo(() => {
    const filtered = filterProfiles(MOCK_PROFILES, filters);
    return sortProfiles(filtered, filters.sort, filters);
  }, [filters]);
  return <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-3706">
        <div className="mb-8" data-id="element-3707">
          <h1 className="text-3xl font-bold text-light-1" data-id="element-3708">Marketplace</h1>
          <p className="text-light-3 mt-2" data-id="element-3709">
            Găsește chiriașul perfect pentru proprietatea ta
          </p>
        </div>

        {/* Onboarding card for supply users */}
        {showOnboarding && <OnboardingCard onDismiss={handleDismissOnboarding} data-id="element-3710" />}

        {/* Filters */}
        <MarketplaceFilters filters={filters} onChange={setFilters} properties={properties} selectedPropertyId={selectedPropertyId} onSelectProperty={handleSelectProperty} resultCount={filteredProfiles.length} onClearAll={handleClearAll} onAddProperty={() => navigate(PAGE_ROUTE.PROPERTY_EDITOR)} isLoggedIn={!!session} data-id="element-3711" />

        {/* Profiles grid */}
        {filteredProfiles.length === 0 ? <EmptyState icon={SearchIcon} title="Niciun chiriaș nu se potrivește cu filtrele selectate" description="Încearcă să relaxezi criteriile de căutare pentru a vedea mai multe rezultate." action={<div className="flex items-center gap-3" data-id="element-3713">
                <Button variant="outline" onClick={handleClearAll} className="border-dark-4 text-light-2 hover:bg-dark-3" data-id="element-3714">
                  Șterge toate filtrele
                </Button>
              </div>} data-id="element-3712" /> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3715">
            {filteredProfiles.map(profile => <ProfileCard key={profile.id} profile={profile} onClick={() => navigate(PAGE_ROUTE.TENANT_ANUNT, {
          slug: profile.slug
        })} onBookmarkBlocked={() => setAuthGate({
          open: true,
          intent: 'bookmark'
        })} data-id="element-3716" />)}
          </div>}
      </div>

      <AuthGateModal isOpen={authGate.open} onClose={() => setAuthGate(s => ({
      ...s,
      open: false
    }))} intent={authGate.intent} navigate={navigate} data-id="element-3717" />
    </>;
}